import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';

// Action types
export const OPEN_REVIEW_REQUEST = 'app/AddMyReviewPage/OPEN_REVIEW_REQUEST';
export const OPEN_REVIEW_SUCCESS = 'app/AddMyReviewPage/OPEN_REVIEW_SUCCESS';
export const OPEN_REVIEW_ERROR = 'app/AddMyReviewPage/OPEN_REVIEW_ERROR';

export const CLOSE_REVIEW_REQUEST = 'app/AddMyReviewPage/CLOSE_REVIEW_REQUEST';
export const CLOSE_REVIEW_SUCCESS = 'app/AddMyReviewPage/CLOSE_REVIEW_SUCCESS';
export const CLOSE_REVIEW_ERROR = 'app/AddMyReviewPage/CLOSE_REVIEW_ERROR';

export const SUBMIT_REVIEW_REQUEST = 'app/AddMyReviewPage/SUBMIT_REVIEW_REQUEST';
export const SUBMIT_REVIEW_SUCCESS = 'app/AddMyReviewPage/SUBMIT_REVIEW_SUCCESS';
export const SUBMIT_REVIEW_ERROR = 'app/AddMyReviewPage/SUBMIT_REVIEW_ERROR';

export const UPLOAD_REVIEW_IMAGE_REQUEST = 'app/AddMyReviewPage/UPLOAD_REVIEW_IMAGE_REQUEST';
export const UPLOAD_REVIEW_IMAGE_SUCCESS = 'app/AddMyReviewPage/UPLOAD_REVIEW_IMAGE_SUCCESS';
export const UPLOAD_REVIEW_IMAGE_ERROR = 'app/AddMyReviewPage/UPLOAD_REVIEW_IMAGE_ERROR';

export const CLEAR_REVIEW_STATE = 'app/AddMyReviewPage/CLEAR_REVIEW_STATE';

// Initial state
const initialState = {
  openReviewInProgress: false,
  openReviewError: null,
  closeReviewInProgress: false,
  closeReviewError: null,
  submitInProgress: false,
  submitError: null,
  submittedReview: null,
  uploadInProgress: false,
  uploadError: null,
  uploadedImages: [],
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case OPEN_REVIEW_REQUEST:
      return {
        ...state,
        openReviewInProgress: true,
        openReviewError: null,
      };

    case OPEN_REVIEW_SUCCESS:
      return {
        ...state,
        openReviewInProgress: false,
      };

    case OPEN_REVIEW_ERROR:
      return {
        ...state,
        openReviewInProgress: false,
        openReviewError: payload,
      };

    case CLOSE_REVIEW_REQUEST:
      return {
        ...state,
        closeReviewInProgress: true,
        closeReviewError: null,
      };

    case CLOSE_REVIEW_SUCCESS:
      return {
        ...state,
        closeReviewInProgress: false,
      };

    case CLOSE_REVIEW_ERROR:
      return {
        ...state,
        closeReviewInProgress: false,
        closeReviewError: payload,
      };

    case SUBMIT_REVIEW_REQUEST:
      return {
        ...state,
        submitInProgress: true,
        submitError: null,
      };

    case SUBMIT_REVIEW_SUCCESS:
      return {
        ...state,
        submitInProgress: false,
        submittedReview: payload.review,
      };

    case SUBMIT_REVIEW_ERROR:
      return {
        ...state,
        submitInProgress: false,
        submitError: payload,
      };

    case UPLOAD_REVIEW_IMAGE_REQUEST:
      return {
        ...state,
        uploadInProgress: true,
        uploadError: null,
      };

    case UPLOAD_REVIEW_IMAGE_SUCCESS:
      return {
        ...state,
        uploadInProgress: false,
        uploadedImages: [...state.uploadedImages, payload.image],
      };

    case UPLOAD_REVIEW_IMAGE_ERROR:
      return {
        ...state,
        uploadInProgress: false,
        uploadError: payload,
      };

    case CLEAR_REVIEW_STATE:
      return initialState;

    default:
      return state;
  }
}

// Action creators
export const openReviewRequest = id => ({
  type: OPEN_REVIEW_REQUEST,
  payload: { id },
});

export const openReviewSuccess = () => {
  return {
    type: OPEN_REVIEW_SUCCESS,
  };
};
export const openReviewError = e => ({
  type: OPEN_REVIEW_ERROR,
  error: true,
  payload: e,
});

export const closeReviewRequest = id => ({
  type: CLOSE_REVIEW_REQUEST,
  payload: { id },
});

export const closeReviewSuccess = () => ({
  type: CLOSE_REVIEW_SUCCESS,
});

export const closeReviewError = e => ({
  type: CLOSE_REVIEW_ERROR,
  error: true,
  payload: e,
});

export const submitReviewRequest = () => ({ type: SUBMIT_REVIEW_REQUEST });
export const submitReviewSuccess = review => ({
  type: SUBMIT_REVIEW_SUCCESS,
  payload: { review },
});
export const submitReviewError = error => ({
  type: SUBMIT_REVIEW_ERROR,
  payload: error,
});

export const uploadReviewImageRequest = () => ({ type: UPLOAD_REVIEW_IMAGE_REQUEST });
export const uploadReviewImageSuccess = image => ({
  type: UPLOAD_REVIEW_IMAGE_SUCCESS,
  payload: { image },
});
export const uploadReviewImageError = error => ({
  type: UPLOAD_REVIEW_IMAGE_ERROR,
  payload: error,
});

export const clearReviewState = () => ({ type: CLEAR_REVIEW_STATE });

// Thunk actions
export const uploadReviewImage = file => (dispatch, getState, sdk) => {
  dispatch(uploadReviewImageRequest());

  return sdk.images
    .upload({ image: file }, { expand: true })
    .then(response => {
      const image = response.data.data;
      dispatch(uploadReviewImageSuccess(image));
      return image;
    })
    .catch(e => {
      console.error('Image upload failed:', e);
      const error = storableError(e);
      dispatch(uploadReviewImageError(error));
      throw e;
    });
};

const getSizeData = (category, sizeValue) => {
  if (category === 'clothing') {
    return { ClothingSize: sizeValue };
  } else if (category === 'shoes') {
    return { ShoeSize: sizeValue };
  }
  return { Size: sizeValue };
};

export const openReview = reviewId => (dispatch, getState, sdk) => {
  dispatch(openReviewRequest(reviewId));

  return sdk.ownListings
    .open({ id: reviewId }, { expand: true })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(openReviewSuccess());
      return response;
    })
    .catch(e => {
      dispatch(openReviewError(storableError(e)));
    });
};

export const closeReview = reviewId => (dispatch, getState, sdk) => {
  dispatch(closeReviewRequest(reviewId));
  return sdk.ownListings
    .close({ id: reviewId }, { expand: true })
    .then(response => {
      dispatch(closeReviewSuccess());
      return response;
    })
    .catch(e => {
      dispatch(closeReviewError(storableError(e)));
    });
};

export const submitReview = reviewData => (dispatch, getState, sdk) => {
  dispatch(submitReviewRequest());

  const {
    id,
    brand,
    customBrand,
    brandSlug,
    productName,
    color,
    size,
    height,
    reviewText,
    userEmail,
    userPassword,
    images = [],
    category,
    slug,
  } = reviewData;

  // Use customBrand if "Other" is selected and customBrand is provided
  const finalBrand = brand === 'Other' && customBrand ? customBrand : brand;

  // Format size for storage - ensure it's a string for consistent display
  // Handle empty array case explicitly
  let formattedSize;
  if (Array.isArray(size)) {
    formattedSize = size.length > 0 ? size.join(', ') : '';
  } else if (typeof size === 'string') {
    formattedSize = size.trim();
  } else {
    formattedSize = '';
  }

  // Create a "review" listing in Sharetribe - matching the import script structure
  const listingData = {
    title: productName,
    description: reviewText,
    publicData: {
      // Match the import script structure
      listingType: 'review',
      transactionProcessAlias: 'default-inquiry/release-1',
      unitType: 'item',

      // Product details
      BrandName: finalBrand || '',
      color: color || '',
      ...getSizeData(category, formattedSize),
      height: height || '',
      slug: slug || '',

      // Review metadata
      reviewerEmail: userEmail,

      // For filtering and searching
      categoryLevel1: category || '',
      brandSlug,
      productTypeSlug: productName?.toLowerCase().includes('dress')
        ? 'dress'
        : productName?.toLowerCase().includes('top')
        ? 'top'
        : productName?.toLowerCase().includes('bottom')
        ? 'bottom'
        : 'other',
    },
    images: images.map(image => image.id),
  };

  if (id) {
    return sdk.ownListings
      .update(
        { ...listingData, id },
        {
          expand: true,
          include: ['images'],
          'fields.image': [
            'variants.scaled-small',
            'variants.scaled-medium',
            'variants.scaled-large',
          ],
        }
      )
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        const review = entities[0];
        dispatch(submitReviewSuccess(review));
        return review;
      })
      .catch(e => {
        console.error('Review submission failed:', e);
        const error = storableError(e);
        dispatch(submitReviewError(error));
        throw e;
      });
  } else {
    return sdk.ownListings
      .create(listingData, {
        expand: true,
        include: ['images'],
        'fields.image': [
          'variants.scaled-small',
          'variants.scaled-medium',
          'variants.scaled-large',
        ],
      })
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        const review = entities[0];
        dispatch(submitReviewSuccess(review));
        return review;
      })
      .catch(e => {
        console.error('Review submission failed:', e);
        const error = storableError(e);
        dispatch(submitReviewError(error));
        throw e;
      });
  }
};

// Selectors
export const getOpenReviewInProgress = state => state.AddMyReviewPage.openReviewInProgress;
export const getOpenReviewError = state => state.AddMyReviewPage.openReviewError;
export const getCloseReviewInProgress = state => state.AddMyReviewPage.closeReviewInProgress;
export const getCloseReviewError = state => state.AddMyReviewPage.closeReviewError;

export const getSubmitInProgress = state => state.AddMyReviewPage?.submitInProgress || false;
export const getSubmitError = state => state.AddMyReviewPage?.submitError || null;
export const getSubmittedReview = state => state.AddMyReviewPage?.submittedReview || null;
export const getUploadInProgress = state => state.AddMyReviewPage?.uploadInProgress || false;
export const getUploadError = state => state.AddMyReviewPage?.uploadError || null;
export const getUploadedImages = state => state.AddMyReviewPage?.uploadedImages || [];
